import { useTranslation } from 'react-i18next';
import { navigate } from 'gatsby';
import { Button, Modal } from '@opswat/ui';
import { type LimitAudience, type RateLimitKind } from 'utils/rateLimit';

interface LimitReachedModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    kind: RateLimitKind;
    audience: LimitAudience;
    /** The backend's own sentence about the rejection; `FALLBACK_BODY` when the response had none. */
    message?: string | null;
}

/**
 * The titles are the raw English sentences, which are also the i18n keys, so `t()` is called on a
 * value looked up at render time rather than on a literal. That is safe here because both are
 * present in `static/locales/en.json` and `static/locales/ja.json` — the set is closed by
 * `RateLimitKind`, so no lookup can miss a key.
 */
const TITLES: Record<RateLimitKind, string> = {
    hourly: 'You are sending requests too quickly',
    daily: 'Daily scan limit reached',
};

/**
 * The body is the backend's message, not ours: it states the actual limit and the tier it was
 * applied to ("maximum requests per hour: 12 for unregistered user"), which stays correct when the
 * backend changes a limit. It is therefore English-only — the platform API does not localise it —
 * and this fallback is used only when a 429 arrives with no message at all (e.g. an edge proxy).
 */
const FALLBACK_BODY = 'You have reached the request limit for scanning.';

const CTAS: Record<LimitAudience, { label: string; to: string }> = {
    unauthenticated: { label: 'Sign In', to: '/login' },
    community: { label: 'See plans', to: '/licensing' },
    commercial: { label: 'Licensing', to: '/licensing' },
};

export const LimitReachedModal = ({ open, onOpenChange, kind, audience, message }: LimitReachedModalProps) => {
    const { t } = useTranslation();

    const handlePrimary = () => {
        onOpenChange(false);
        navigate(CTAS[audience].to);
    };

    // Always returns the `Modal` element rather than `null` when `open` is `false`: the `@opswat/ui`
    // `Modal` is Radix-backed and renders nothing at all while closed, so an early return would only
    // duplicate that and would drop Radix's open/close transition. Same shape as
    // `src/components/demo/RequestDemoModal.tsx` and `src/components/details/PasswordProtectedPrompt.tsx`.
    return (
        <Modal open={open} onOpenChange={onOpenChange} hasCloseButton>
            <Modal.Content className='tw:w-full tw:max-w-480'>
                {/* `Modal.Title` is the only heading in here, so it is what names the dialog for a
                    screen reader. The a11y workflow sweeps static routes and never opens this
                    modal, so the accessible name has to be correct by construction. */}
                <Modal.Title>{t(TITLES[kind])}</Modal.Title>
                <p className='tw:text-body tw:text-neutrals-700 tw:dark:text-neutrals-300 tw:mt-8'>
                    {message || t(FALLBACK_BODY)}
                </p>
                <div className='tw:flex tw:justify-end tw:gap-8 tw:mt-24'>
                    <Button
                        id='limit-reached-dismiss'
                        variant='secondary'
                        text={t('Close')}
                        onClick={() => onOpenChange(false)}
                    />
                    <Button
                        id='limit-reached-primary'
                        variant='primary'
                        text={t(CTAS[audience].label)}
                        onClick={handlePrimary}
                    />
                </div>
            </Modal.Content>
        </Modal>
    );
};
